<script setup lang="ts">
import { useI18n } from "vue-i18n";

import tailwindConfig from "../../tailwind.config";

const themeColors = tailwindConfig.theme.extend.colors;

const { t } = useI18n();

export type ButtonComponentProperties = {
  disabled?: boolean;
  primary?: boolean;
  full?: boolean;
  isLoading?: boolean;
  style?: "default" | "success" | "disruptive";
};

type ButtonColors = {
  accent: string;
  hover: string;
};

const props = withDefaults(defineProps<ButtonComponentProperties>(), {
  style: "default",
});

const isDisabled = computed(() => props.disabled || props.isLoading);
const ariaLabel = computed(() => props.isLoading ? t("general.loading") : undefined);

const buttonColor = computed<ButtonColors>(() => {
  const isPrimary = props.primary;
  return {
    ["default"]: {
      accent: themeColors["ppa-dark-blue"],
      hover: isPrimary ? themeColors["ppa-darker-blue"] : themeColors["ppa-light-slate"]},
    ["success"]: {
      accent: themeColors["ppa-green"],
      hover: isPrimary ? themeColors["ppa-dark-green"] : themeColors["ppa-light-green"],
    },
    ["disruptive"]: {
      accent: themeColors["ppa-red"],
      hover: isPrimary ? themeColors["ppa-dark-red"] : themeColors["ppa-light-rose"],
    },
  }[props.style];
});

const loadingIconColor = computed(() => props.primary ? themeColors["ppa-white"] : buttonColor.value.accent);

const buttonClasses = computed(() => {
  return {
    "w-full": props.full,
    "bg-white text-[var(--accent-color)] disabled:opacity-60": !props.primary,
    "bg-[var(--accent-color)] text-white disabled:border-[var(--accent-color)] disabled:text-white disabled:opacity-60": props.primary,
    "cursor-not-allowed": isDisabled.value,
    "hover:bg-[var(--hover-color)]": !isDisabled.value,
    "hover:border-[--hover-color]": !isDisabled.value && props.primary,
  };
});

</script>

<template>
  <button class="flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold whitespace-nowrap rounded-3xl border-2 border-[var(--accent-color)] transition-colors"
    :style="{ '--accent-color': buttonColor.accent, '--hover-color': buttonColor.hover }"  
    :disabled="isDisabled"
    :class="buttonClasses"
    :aria-label="ariaLabel"
    :aria-busy="isLoading"
  >
    <slot v-if="isLoading" name="loading-indicator">
      <IconLoadingCircle :color="loadingIconColor"/>
    </slot>  
    <slot v-else name="icon"></slot>
    <slot></slot>
  </button>
</template>
